import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Loader2, Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import orchardService from '../../services/orchard';
import PbrMark from '../cultivar/PbrMark';
import {
  cultivarDisplayName,
  draftCanLayOut,
  draftToSave,
  type PlanDraft,
} from './plantingPlanDraft';
import type { GeoJSONPolygon, PlantingPlan, Pollinator, RowGroup } from '../../types/orchard';

// -----------------------------------------------------------------------------
// Inline cultivar picker (debounced search against /orchard/cultivars/search).
// -----------------------------------------------------------------------------

type CultivarOption = {
  id: number;
  name: string;
  trade_name: string | null;
  crop_type_name: string | null;
  protection_status?: string | null;
};

export function PlanCultivarPicker({
  value,
  valueName,
  onChange,
  placeholder = 'Search cultivar…',
  compact = false,
}: {
  value: number | null;
  valueName?: string | null;
  onChange: (option: CultivarOption | null) => void;
  placeholder?: string;
  compact?: boolean;
}) {
  const [query, setQuery] = useState<string>(valueName || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<CultivarOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (valueName) setQuery(valueName);
  }, [valueName]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!open || query.length < 1) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const res = await orchardService.searchCultivars(query, 10);
        if (res.success && res.data) setResults(res.data.cultivars);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <input
          className={`input-professional w-full pl-8 ${compact ? 'py-1.5 text-sm' : ''}`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-neutral-400" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-[60] w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-primary-50 text-sm border-b border-neutral-100 last:border-0"
              onMouseDown={(e) => {
                e.preventDefault();
                const display = c.trade_name ? `${c.name} (${c.trade_name})` : c.name;
                setQuery(display);
                setOpen(false);
                onChange(c);
              }}
            >
              <div className="font-medium">
                {c.name}
                <PbrMark status={c.protection_status} />
              </div>
              {c.trade_name && <div className="text-xs text-neutral-500">{c.trade_name}</div>}
            </button>
          ))}
        </div>
      )}
      {value !== null && !valueName && !query && (
        <p className="text-[11px] text-neutral-500 mt-1">Cultivar #{value}</p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pollinator row editor — reused for area defaults + per-row overrides.
// -----------------------------------------------------------------------------

export function PollinatorList({
  pollinators,
  cultivarNameLookup,
  onChange,
  onPicked,
}: {
  pollinators: Pollinator[];
  cultivarNameLookup: (id: number) => string;
  onChange: (next: Pollinator[]) => void;
  onPicked?: (id: number, name: string) => void;
}) {
  const update = (idx: number, patch: Partial<Pollinator>) => {
    onChange(pollinators.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const remove = (idx: number) => onChange(pollinators.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...pollinators,
      { cultivar_id: 0, first_position: 10, interval: 20, label: `Pollinator ${pollinators.length + 1}` },
    ]);

  return (
    <div className="space-y-2">
      {pollinators.length === 0 && (
        <p className="text-xs text-neutral-500 italic">No pollinators in the rows.</p>
      )}
      {pollinators.map((p, idx) => (
        <div
          key={idx}
          className="grid grid-cols-6 sm:grid-cols-12 gap-2 items-end bg-neutral-50 p-2 rounded-lg border border-neutral-200"
        >
          <div className="col-span-5 sm:col-span-6">
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">
              {p.label || `Pollinator ${idx + 1}`}
            </label>
            <PlanCultivarPicker
              compact
              value={p.cultivar_id || null}
              valueName={p.cultivar_id ? cultivarNameLookup(p.cultivar_id) || null : null}
              onChange={(opt) => {
                if (opt) onPicked?.(opt.id, opt.trade_name ? `${opt.name} (${opt.trade_name})` : opt.name);
                update(idx, { cultivar_id: opt ? opt.id : 0 });
              }}
            />
          </div>
          <div className="col-span-1 sm:hidden flex justify-end">
            <button
              type="button"
              onClick={() => remove(idx)}
              className="p-1.5 rounded hover:bg-error-100 text-error-600"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">1st tree</label>
            <input
              type="number"
              min={1}
              className="input-professional w-full py-1.5 text-sm"
              value={p.first_position}
              onChange={(e) => update(idx, { first_position: parseInt(e.target.value) || 1 })}
            />
          </div>
          <div className="col-span-3 sm:col-span-3">
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">Every N trees</label>
            <input
              type="number"
              min={1}
              className="input-professional w-full py-1.5 text-sm"
              value={p.interval}
              onChange={(e) => update(idx, { interval: parseInt(e.target.value) || 1 })}
            />
          </div>
          <div className="hidden sm:flex sm:col-span-1 justify-end">
            <button
              type="button"
              onClick={() => remove(idx)}
              className="p-1.5 rounded hover:bg-error-100 text-error-600"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        <Plus className="h-4 w-4" /> Add pollinator
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// The plan itself: row 1, spacing, pattern, pollinators — and what they add up
// to, laid out by the server as the fields change.
// -----------------------------------------------------------------------------

interface PlantingPlanFieldsProps {
  draft: PlanDraft;
  onChange: (next: PlanDraft) => void;
  /** The planting's boundary. Rows are clipped to it, and a blank row count
   *  fills it. */
  boundary: GeoJSONPolygon | null;
  /** The main cultivar — every row not claimed by the pattern. */
  cultivarId: number | null;
  cultivarName?: string | null;
  /** A saved area, so the preview applies its per-row overrides. */
  areaId?: number;
  drawingFirstRow: boolean;
  onRequestDrawFirstRow: () => void;
  onCancelDrawFirstRow: () => void;
  /** The latest layout, for the parent's map. Null while nothing can be laid out. */
  onPreview?: (plan: PlantingPlan | null) => void;
}

function PlantingPlanFields({
  draft,
  onChange,
  boundary,
  cultivarId,
  cultivarName,
  areaId,
  drawingFirstRow,
  onRequestDrawFirstRow,
  onCancelDrawFirstRow,
  onPreview,
}: PlantingPlanFieldsProps) {
  const [preview, setPreview] = useState<PlantingPlan | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [pickedNames, setPickedNames] = useState<Record<number, string>>({});

  // Held in a ref: parents pass an inline callback, and depending on it would
  // re-run the preview on every parent render — the loop that left the old
  // editor spinning forever.
  const onPreviewRef = useRef(onPreview);
  useEffect(() => {
    onPreviewRef.current = onPreview;
  });

  const canLayOut = draftCanLayOut(draft);
  const requestKey = JSON.stringify({
    ...draftToSave(draft),
    boundary_geometry: boundary,
    cultivar_id: cultivarId,
    area_id: areaId ?? null,
  });

  useEffect(() => {
    if (!canLayOut) {
      setPreview(null);
      setPreviewing(false);
      onPreviewRef.current?.(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await orchardService.previewPlantingPlan(JSON.parse(requestKey));
        if (cancelled) return;
        if (res.success && res.data) {
          setPreview(res.data);
          setPreviewError(null);
          onPreviewRef.current?.(res.data);
        } else {
          setPreviewError('Could not lay out the rows.');
        }
      } catch (e: any) {
        if (!cancelled) setPreviewError(e?.message || 'Could not lay out the rows.');
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestKey, canLayOut]);

  const nameLookup = useMemo(() => {
    const m = new Map<number, string>();
    preview?.cultivars.forEach((c) => m.set(c.id, cultivarDisplayName(c)));
    Object.entries(pickedNames).forEach(([id, n]) => m.set(Number(id), n));
    if (cultivarId && cultivarName) m.set(cultivarId, cultivarName);
    return (id: number) => m.get(id) || '';
  }, [preview, pickedNames, cultivarId, cultivarName]);

  const remember = (id: number, name: string) => setPickedNames((prev) => ({ ...prev, [id]: name }));
  const set = (patch: Partial<PlanDraft>) => onChange({ ...draft, ...patch });

  const updateGroup = (idx: number, patch: Partial<RowGroup>) =>
    set({ planting_pattern: draft.planting_pattern.map((g, i) => (i === idx ? { ...g, ...patch } : g)) });

  const plan = preview?.plan;
  const fitted = plan?.fitted_row_count ?? null;
  const patternPeriod = draft.planting_pattern.reduce((s, g) => s + (g.row_count || 0), 0);
  const effectiveRows = plan?.effective_row_count ?? draft.row_count;
  const hasFirstRow = Boolean(draft.first_row_start_point && draft.first_row_end_point);

  return (
    <div className="space-y-4">
      {/* ---------- Row 1 + spacing ---------- */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-900">Rows</h3>
            {drawingFirstRow ? (
              <Button type="button" variant="outline" size="sm" onClick={onCancelDrawFirstRow}>
                Cancel drawing
              </Button>
            ) : (
              <Button
                type="button"
                variant={hasFirstRow ? 'outline' : 'primary'}
                size="sm"
                onClick={onRequestDrawFirstRow}
                disabled={!boundary}
              >
                {hasFirstRow ? 'Redraw row 1' : 'Draw row 1 on map'}
              </Button>
            )}
          </div>

          {drawingFirstRow ? (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Tap two points along row 1 on the map. Anywhere along the row will do — every row
              runs the full length of the boundary, and the rest are laid out beside it into the
              block.
            </p>
          ) : !boundary ? (
            <p className="text-xs text-neutral-500">Draw the boundary first — the rows are laid out inside it.</p>
          ) : !hasFirstRow ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Row 1 sets the direction the rows run. Draw it along the edge row, then set the
              spacing.
            </p>
          ) : (
            <p className="text-xs text-neutral-600">
              Rows run at{' '}
              <strong>{plan?.row_bearing_degrees != null ? `${plan.row_bearing_degrees.toFixed(0)}°` : '—'}</strong>{' '}
              from north.
            </p>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">Row spacing (m)</label>
              <input
                type="number"
                step="0.1"
                min={0}
                className="input-professional w-full py-1.5 text-sm"
                value={draft.row_spacing_meters || ''}
                onChange={(e) => set({ row_spacing_meters: parseFloat(e.target.value) || 0 })}
                placeholder="4.0"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">Tree spacing (m)</label>
              <input
                type="number"
                step="0.1"
                min={0}
                className="input-professional w-full py-1.5 text-sm"
                value={draft.tree_spacing_meters || ''}
                onChange={(e) => set({ tree_spacing_meters: parseFloat(e.target.value) || 0 })}
                placeholder="1.0"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-600 mb-1">Rows</label>
              <input
                type="number"
                min={0}
                className="input-professional w-full py-1.5 text-sm"
                value={draft.row_count || ''}
                onChange={(e) => set({ row_count: parseInt(e.target.value) || 0 })}
                placeholder={fitted ? `${fitted} fit` : 'Fill'}
              />
            </div>
          </div>

          {fitted != null && fitted > 0 && (
            <p className="text-xs text-neutral-500">
              {draft.row_count > 0 ? (
                draft.row_count === fitted ? (
                  <>{fitted} rows fill the boundary.</>
                ) : (
                  <>
                    {fitted} rows fit the boundary at this spacing.{' '}
                    <button
                      type="button"
                      className="text-primary-600 hover:underline font-medium"
                      onClick={() => set({ row_count: 0 })}
                    >
                      Fill the boundary instead
                    </button>
                  </>
                )
              ) : (
                <>Leave Rows blank to fill the boundary — {fitted} rows fit at this spacing.</>
              )}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ---------- Row pattern ---------- */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Row pattern</h3>
            <p className="text-xs text-neutral-500">
              Only needed when rows alternate cultivars — e.g. 4 × Pink Lady, 1 × Gala, repeated
              across the block. With no pattern every row is{' '}
              {cultivarName ? <strong>{cultivarName}</strong> : 'the main cultivar'}.
            </p>
          </div>
          {draft.planting_pattern.map((g, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-end bg-neutral-50 p-2 rounded-lg border border-neutral-200"
            >
              <div className="col-span-7 sm:col-span-8">
                <label className="block text-[11px] font-semibold text-neutral-600 mb-1">Cultivar</label>
                <PlanCultivarPicker
                  compact
                  value={g.cultivar_id}
                  valueName={g.cultivar_id ? nameLookup(g.cultivar_id) || null : null}
                  onChange={(opt) => {
                    if (opt) remember(opt.id, opt.trade_name ? `${opt.name} (${opt.trade_name})` : opt.name);
                    updateGroup(idx, { cultivar_id: opt ? opt.id : null });
                  }}
                />
              </div>
              <div className="col-span-3">
                <label className="block text-[11px] font-semibold text-neutral-600 mb-1">× rows</label>
                <input
                  type="number"
                  min={1}
                  className="input-professional w-full py-1.5 text-sm"
                  value={g.row_count}
                  onChange={(e) => updateGroup(idx, { row_count: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => set({ planting_pattern: draft.planting_pattern.filter((_, i) => i !== idx) })}
                  className="p-1.5 rounded hover:bg-error-100 text-error-600"
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              set({ planting_pattern: [...draft.planting_pattern, { cultivar_id: cultivarId, row_count: 1 }] })
            }
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <Plus className="h-4 w-4" /> Add row group
          </button>
          {patternPeriod > 0 && effectiveRows > 0 && effectiveRows % patternPeriod !== 0 && (
            <p className="text-[11px] text-amber-600">
              The pattern repeats every {patternPeriod} rows, which doesn't divide {effectiveRows} —
              the last repeat is cut short.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ---------- Pollinators ---------- */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Pollinators in the row</h3>
            <p className="text-xs text-neutral-500">
              Trees of another cultivar planted within every row — the first at tree N, then every
              N trees after it.
            </p>
          </div>
          <PollinatorList
            pollinators={draft.default_pollinators}
            cultivarNameLookup={nameLookup}
            onChange={(next) => set({ default_pollinators: next })}
            onPicked={remember}
          />
        </CardContent>
      </Card>

      {/* ---------- What it adds up to ---------- */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold text-neutral-900">What this plants</h3>
            {previewing && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
          </div>
          {previewError ? (
            <p className="text-sm text-error-700">{previewError}</p>
          ) : !canLayOut ? (
            <p className="text-xs text-neutral-500">
              Draw row 1 and set the row spacing to see the rows and tree count.
            </p>
          ) : !plan ? (
            <p className="text-xs text-neutral-500">Laying out rows…</p>
          ) : (
            <>
              <p className="text-sm text-neutral-700 mb-3">
                <strong className="text-lg text-neutral-900">{plan.total_trees.toLocaleString()}</strong> trees in{' '}
                <strong>{plan.effective_row_count}</strong> rows
                {plan.row_count_source === 'fitted' && ' (filling the boundary)'}
                {!draft.tree_spacing_meters && (
                  <span className="block text-xs text-amber-700 mt-1">
                    Set the tree spacing to count trees along each row.
                  </span>
                )}
              </p>
              {preview && preview.summary.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b">
                      <th className="py-1 pr-2">Cultivar</th>
                      <th className="py-1 pr-2 text-right">Main</th>
                      <th className="py-1 pr-2 text-right">Pollinator</th>
                      <th className="py-1 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.summary.map((s) => (
                      <tr key={s.cultivar_id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2 break-words">{nameLookup(s.cultivar_id) || `#${s.cultivar_id}`}</td>
                        <td className="py-1.5 pr-2 text-right">{s.primary_trees.toLocaleString()}</td>
                        <td className="py-1.5 pr-2 text-right">{s.pollinator_trees.toLocaleString()}</td>
                        <td className="py-1.5 text-right font-semibold">{s.total_trees.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PlantingPlanFields;
